import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { ArticleFilter, ArticleUpdateData, ArticlesState, Article } from "../../../types/article.types";
import apiClient from "../../../api/apiClient";
import { API_ENDPOINTS } from "../../../api/endPoints";

// Initial state
const initialState: ArticlesState = {
  items: [],
  filteredItems: [],
  isLoading: false,
  error: null,
  filters: {
    publisherPK: "",
    websitePK: "",
    fromDate: "",
    toDate: "",
    status: "",
    searchTerm: "",
    priority: "", // Added priority filter
  },
  pagination: {
    page: 0,
    rowsPerPage: 10,
    totalCount: 0,
  },
  sortConfig: {
    key: "publishDate",
    direction: "desc",
  },
};

// Async thunks
export const fetchArticles = createAsyncThunk("articles/fetchArticles", async () => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ARTICLES.LIST);
  return response.data;
});

export const fetchFilteredArticles = createAsyncThunk(
  "articles/fetchFilteredArticles",
  async (filters: ArticleFilter) => {
    const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ARTICLES.FILTER, {
      params: filters,
    });
    return response.data;
  }
);

export const deleteFilteredArticles = createAsyncThunk(
  "articles/deleteFilteredArticles",
  async (filters: ArticleFilter) => {
    const response = await apiClient.delete(API_ENDPOINTS.PUBLISHER.ARTICLES.DELETE_BY_FILTER, {
      params: filters,
    });
    return response.data;
  }
);

export const fetchArticlesByPublisher = createAsyncThunk(
  "articles/fetchArticlesByPublisher",
  async (publisherId: string) => {
    const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.ARTICLES.BY_PUBLISHER(publisherId));
    return response.data;
  }
);

export const updateArticle = createAsyncThunk(
  "articles/updateArticle",
  async ({ id, data }: { id: string; data: ArticleUpdateData }) => {
    const response = await apiClient.patch(API_ENDPOINTS.PUBLISHER.ARTICLES.UPDATE(id), data);
    return response.data;
  }
);

export const deleteArticle = createAsyncThunk("articles/deleteArticle", async (id: string) => {
  await apiClient.delete(API_ENDPOINTS.PUBLISHER.ARTICLES.DELETE(id));
  return id;
});

// Slice
const articlesSlice = createSlice({
  name: "articles",
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<{ key: keyof ArticleFilter; value: string }>) => {
      const { key, value } = action.payload;
      if (key === 'priority') {
        state.filters.priority = value ? (parseInt(value, 10) as 1 | 2 | 3) : '';
      } else {
        (state.filters as any)[key] = value;
      }

      // Reset pagination
      state.pagination.page = 0;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.pagination.rowsPerPage = action.payload;
      state.pagination.page = 0;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 0;
    },
    setSortConfig: (state, action: PayloadAction<{ key: string; direction: "asc" | "desc" }>) => {
      state.sortConfig = action.payload;
    },
    addArticles: (state, action: PayloadAction<Article[]>) => {
      const newArticles = action.payload;
      // Prepend new articles to items and filteredItems
      state.items = [...newArticles, ...state.items];
      state.filteredItems = [...newArticles, ...state.filteredItems];
      state.pagination.totalCount += newArticles.length;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all articles
      .addCase(fetchArticles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch articles";
      })

      // Fetch filtered articles
      .addCase(fetchFilteredArticles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFilteredArticles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchFilteredArticles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch filtered articles";
      })

      // Delete filtered articles
      .addCase(deleteFilteredArticles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteFilteredArticles.fulfilled, (state, action) => {
        state.isLoading = false;
        // Clear filtered items based on the response
        state.filteredItems = [];
        state.pagination.totalCount = 0;

        // Reset to page 0 since we deleted all filtered items
        state.pagination.page = 0;
      })
      .addCase(deleteFilteredArticles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to delete filtered articles";
      })

      // Fetch articles by publisher
      .addCase(fetchArticlesByPublisher.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArticlesByPublisher.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchArticlesByPublisher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch articles by publisher";
      })

      // Update article
      .addCase(updateArticle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateArticle.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedArticle = action.payload;

        // Update in items array
        const itemIndex = state.items.findIndex((item) => item.id === updatedArticle.id);
        if (itemIndex !== -1) {
          state.items[itemIndex] = updatedArticle;
        }

        // Update in filtered items array
        const filteredIndex = state.filteredItems.findIndex((item) => item.id === updatedArticle.id);
        if (filteredIndex !== -1) {
          state.filteredItems[filteredIndex] = updatedArticle;
        }
      })
      .addCase(updateArticle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to update article";
      })

      // Delete article
      .addCase(deleteArticle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteArticle.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedId = action.payload;

        // Remove from items array
        state.items = state.items.filter((item) => item.id !== deletedId);

        // Remove from filtered items array
        state.filteredItems = state.filteredItems.filter((item) => item.id !== deletedId);

        state.pagination.totalCount = state.filteredItems.length;
      })
      .addCase(deleteArticle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to delete article";
      });
  },
});

export const { setFilter, setPage, setRowsPerPage, resetFilters, setSortConfig, addArticles } = articlesSlice.actions;

export default articlesSlice.reducer;
